import { CERAMIC_BOOKING_DEPOSIT_CENTS } from './booking-domain.ts'
import { canWriteXeroInvoices, classifyXeroOrganisation } from './xero-oauth.ts'

export const XERO_DEPOSIT_CREDIT_CENTS = CERAMIC_BOOKING_DEPOSIT_CENTS

export const XERO_RECONCILIATION_STATUSES = [
  'not_ready',
  'ready',
  'invoice_created',
  'deposit_applied',
  'complete',
  'failed',
] as const

export type XeroReconciliationStatus = (typeof XERO_RECONCILIATION_STATUSES)[number]

export interface XeroContactRecord {
  contactId: string
  name: string
  email: string
  phone?: string
}

export interface XeroInvoiceRecord {
  invoiceId: string
  invoiceNumber: string
  totalCents: number
  amountPaidCents: number
  status: string
}

export interface XeroClient {
  getOrganisation(): Promise<{ isDemoCompany: boolean | null; name: string }>
  findContactByEmail(email: string): Promise<XeroContactRecord | null>
  createContact(input: { name: string; email: string; phone?: string }): Promise<XeroContactRecord>
  createInvoice(input: {
    contactId: string
    amountCents: number
    description: string
    reference: string
    accountCode: string
  }): Promise<XeroInvoiceRecord>
  getInvoice(invoiceId: string): Promise<XeroInvoiceRecord>
  createPayment(input: {
    invoiceId: string
    amountCents: number
    accountCode: string
    reference: string
  }): Promise<{ paymentId: string }>
  getOnlineInvoiceUrl?(invoiceId: string): Promise<string | null>
}

export interface ReconciliationBooking {
  id: string
  publicReference: string
  paymentStatus?: string | null
  customer: {
    name: string
    email: string
    phone?: string
  }
}

export interface ExistingXeroSync {
  status?: string | null
  contactId?: string | null
  invoiceId?: string | null
  invoiceNumber?: string | null
  paymentId?: string | null
  balancePaymentId?: string | null
  onlineInvoiceUrl?: string | null
}

export interface AdminInvoiceRequest {
  bookingId: string
  finalAmountCents: number
  description?: string
}

export interface XeroInvoiceConfig {
  salesAccountCode: string
  depositAccountCode: string
  allowLiveWrites: boolean
}

export type ReconciliationResult =
  | {
      ok: true
      idempotent?: boolean
      status: XeroReconciliationStatus
      contactId: string
      invoiceId: string
      invoiceNumber: string
      paymentId: string | null
      invoiceTotalCents: number
      depositAppliedCents: number
      outstandingCents: number
    }
  | {
      ok: false
      reason: 'unpaid' | 'invalid_amount' | 'missing_customer' | 'live_tenant_blocked' | 'not_configured' | 'failed'
      error?: string
      status: XeroReconciliationStatus
    }

export function customerDepositCreditCents(_stripeFeeCents?: number) {
  return XERO_DEPOSIT_CREDIT_CENTS
}

export function outstandingBalanceCents(
  invoiceTotalCents: number,
  depositCreditCents = XERO_DEPOSIT_CREDIT_CENTS,
) {
  return remainingBalanceCents(invoiceTotalCents, depositCreditCents)
}

export function remainingBalanceCents(invoiceTotalCents: number, amountPaidCents: number) {
  return Math.max(0, invoiceTotalCents - amountPaidCents)
}

export function squareBalancePaymentReference(publicReference: string, squarePaymentId: string) {
  return `Square ${publicReference} ${squarePaymentId}`
}

export function invoiceReference(publicReference: string) {
  return `Autoglow booking ${publicReference}`
}

export function paymentReference(publicReference: string) {
  return `Stripe deposit ${publicReference}`
}

export function parseAudAmountToCents(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.round(value * 100)
  }
  if (typeof value !== 'string') return null
  const cleaned = value.trim().replace(/[$,]/g, '')
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null
  const [dollars, cents = ''] = cleaned.split('.')
  return Number(dollars) * 100 + Number(cents.padEnd(2, '0'))
}

export function parseAdminInvoiceRequest(body: unknown): {
  ok: true
  request: AdminInvoiceRequest
} | {
  ok: false
  error: string
} {
  if (!body || typeof body !== 'object') {
    return { ok: false, error: 'A booking and final job amount are required.' }
  }

  const payload = body as Record<string, unknown>
  const bookingId = typeof payload.bookingId === 'string' ? payload.bookingId.trim() : ''
  if (!bookingId) return { ok: false, error: 'A booking id is required.' }

  const amountCents = typeof payload.finalAmountCents === 'number' && Number.isInteger(payload.finalAmountCents)
    ? payload.finalAmountCents
    : parseAudAmountToCents(payload.finalAmountAud)

  if (!amountCents || amountCents <= XERO_DEPOSIT_CREDIT_CENTS) {
    return { ok: false, error: 'Final job amount must be greater than the A$100 deposit.' }
  }

  const description = typeof payload.description === 'string' ? payload.description.trim() : ''

  return {
    ok: true,
    request: {
      bookingId,
      finalAmountCents: amountCents,
      ...(description ? { description } : {}),
    },
  }
}

function defaultDescription(booking: ReconciliationBooking) {
  return `Ceramic protection — ${invoiceReference(booking.publicReference)}`
}

export async function reconcileFinalInvoice(input: {
  xero: XeroClient
  booking: ReconciliationBooking
  existingSync: ExistingXeroSync | null
  request: AdminInvoiceRequest
  config: XeroInvoiceConfig
}): Promise<ReconciliationResult> {
  if (input.booking.paymentStatus !== 'paid') {
    return { ok: false, reason: 'unpaid', status: 'not_ready' }
  }

  const email = input.booking.customer.email.trim().toLowerCase()
  const name = input.booking.customer.name.trim()
  if (!email || !name) {
    return { ok: false, reason: 'missing_customer', status: 'not_ready' }
  }

  if (input.request.finalAmountCents <= XERO_DEPOSIT_CREDIT_CENTS) {
    return { ok: false, reason: 'invalid_amount', status: 'not_ready' }
  }

  const organisation = await input.xero.getOrganisation()
  if (!canWriteXeroInvoices({
    organisationClassification: classifyXeroOrganisation(organisation.isDemoCompany),
    allowLiveWrites: input.config.allowLiveWrites,
  })) {
    return { ok: false, reason: 'live_tenant_blocked', status: 'not_ready' }
  }

  const depositAppliedCents = customerDepositCreditCents()
  const existingPaymentId = input.existingSync?.paymentId ?? null
  const existingInvoiceId = input.existingSync?.invoiceId ?? null

  let contactId = input.existingSync?.contactId ?? null
  if (!contactId) {
    const existing = await input.xero.findContactByEmail(email)
    const contact = existing ?? await input.xero.createContact({
      name,
      email,
      phone: input.booking.customer.phone,
    })
    contactId = contact.contactId
  }

  let invoiceId = existingInvoiceId
  let invoiceNumber = input.existingSync?.invoiceNumber ?? ''
  let invoiceTotalCents = input.request.finalAmountCents

  if (!invoiceId) {
    const invoice = await input.xero.createInvoice({
      contactId,
      amountCents: input.request.finalAmountCents,
      description: input.request.description || defaultDescription(input.booking),
      reference: invoiceReference(input.booking.publicReference),
      accountCode: input.config.salesAccountCode,
    })
    invoiceId = invoice.invoiceId
    invoiceNumber = invoice.invoiceNumber
    invoiceTotalCents = invoice.totalCents
  } else {
    const invoice = await input.xero.getInvoice(invoiceId)
    invoiceNumber = invoice.invoiceNumber
    invoiceTotalCents = invoice.totalCents
  }

  const currentInvoice = await input.xero.getInvoice(invoiceId)
  invoiceTotalCents = currentInvoice.totalCents
  invoiceNumber = currentInvoice.invoiceNumber

  if (existingPaymentId || currentInvoice.amountPaidCents >= depositAppliedCents) {
    const outstandingCents = remainingBalanceCents(invoiceTotalCents, currentInvoice.amountPaidCents)
    return {
      ok: true,
      idempotent: true,
      status: outstandingCents <= 0 ? 'complete' : 'deposit_applied',
      contactId,
      invoiceId,
      invoiceNumber,
      paymentId: existingPaymentId,
      invoiceTotalCents,
      depositAppliedCents,
      outstandingCents,
    }
  }

  const payment = await input.xero.createPayment({
    invoiceId,
    amountCents: depositAppliedCents,
    accountCode: input.config.depositAccountCode,
    reference: paymentReference(input.booking.publicReference),
  })

  const paidInvoice = await input.xero.getInvoice(invoiceId)
  const outstandingCents = remainingBalanceCents(paidInvoice.totalCents, paidInvoice.amountPaidCents)

  return {
    ok: true,
    idempotent: Boolean(existingInvoiceId),
    status: outstandingCents <= 0 ? 'complete' : 'deposit_applied',
    contactId,
    invoiceId,
    invoiceNumber: paidInvoice.invoiceNumber,
    paymentId: payment.paymentId,
    invoiceTotalCents: paidInvoice.totalCents,
    depositAppliedCents,
    outstandingCents,
  }
}

export async function refreshXeroInvoiceSettlement(input: {
  xero: XeroClient
  invoiceId: string
}) {
  const invoice = await input.xero.getInvoice(input.invoiceId)
  const outstandingCents = remainingBalanceCents(invoice.totalCents, invoice.amountPaidCents)
  const onlineInvoiceUrl = input.xero.getOnlineInvoiceUrl
    ? await input.xero.getOnlineInvoiceUrl(input.invoiceId)
    : null
  return {
    invoice,
    outstandingCents,
    paid: outstandingCents <= 0,
    onlineInvoiceUrl,
  }
}

export async function applyVerifiedBalancePayment(input: {
  xero: XeroClient
  invoiceId: string
  amountCents: number
  accountCode: string
  reference: string
  existingBalancePaymentId?: string | null
}): Promise<{
  ok: true
  idempotent: boolean
  paymentId: string | null
  invoiceTotalCents: number
  amountPaidCents: number
  outstandingCents: number
  alreadyPaid: boolean
} | {
  ok: false
  reason: 'amount_mismatch' | 'missing_invoice'
  error: string
}> {
  const invoice = await input.xero.getInvoice(input.invoiceId)
  const outstandingCents = remainingBalanceCents(invoice.totalCents, invoice.amountPaidCents)

  if (input.existingBalancePaymentId || outstandingCents <= 0) {
    return {
      ok: true,
      idempotent: true,
      paymentId: input.existingBalancePaymentId ?? null,
      invoiceTotalCents: invoice.totalCents,
      amountPaidCents: invoice.amountPaidCents,
      outstandingCents,
      alreadyPaid: outstandingCents <= 0,
    }
  }

  if (input.amountCents !== outstandingCents) {
    return {
      ok: false,
      reason: 'amount_mismatch',
      error: 'Square payment must equal the current Xero outstanding balance.',
    }
  }

  const payment = await input.xero.createPayment({
    invoiceId: input.invoiceId,
    amountCents: input.amountCents,
    accountCode: input.accountCode,
    reference: input.reference,
  })
  const paidInvoice = await input.xero.getInvoice(input.invoiceId)
  return {
    ok: true,
    idempotent: false,
    paymentId: payment.paymentId,
    invoiceTotalCents: paidInvoice.totalCents,
    amountPaidCents: paidInvoice.amountPaidCents,
    outstandingCents: remainingBalanceCents(paidInvoice.totalCents, paidInvoice.amountPaidCents),
    alreadyPaid: remainingBalanceCents(paidInvoice.totalCents, paidInvoice.amountPaidCents) <= 0,
  }
}
